import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { 
  Heart, Gift, Users, ArrowRight, Loader2, Mail, 
  Check, Globe
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const donationPackages = [
  { id: "small", amount: 10, label: "Supporter" },
  { id: "medium", amount: 25, label: "Patron" },
  { id: "large", amount: 50, label: "Benefactor" },
  { id: "xl", amount: 100, label: "Champion" }
];

const volunteerRoles = [
  { role: "Event Coordination", desc: "Help organize screenings, meetups, and virtual events" },
  { role: "Content Writing", desc: "Write news articles, reviews, and filmmaker profiles" },
  { role: "Social Media", desc: "Manage our social presence and community engagement" },
  { role: "Community Moderation", desc: "Keep the forum welcoming and on-topic" },
  { role: "Technical Support", desc: "Help maintain the website and member tools" },
  { role: "Festival Coverage", desc: "Report on AI film festivals around the world" }
];

export default function Support() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [donationAmount, setDonationAmount] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("donate");
  
  // Volunteer form state
  const [volunteerForm, setVolunteerForm] = useState({
    name: "",
    email: "",
    role: "",
    message: ""
  });
  const [submittingVolunteer, setSubmittingVolunteer] = useState(false);

  // Check for donation success/cancel from Stripe redirect
  useEffect(() => {
    const donationStatus = searchParams.get("donation");
    const sessionId = searchParams.get("session_id");
    
    if (donationStatus === "success" && sessionId) {
      checkPaymentStatus(sessionId);
    } else if (donationStatus === "cancelled") {
      toast.info("Donation cancelled. No payment was made.");
    }
  }, [searchParams]);

  // Pre-fill volunteer form with user info
  useEffect(() => {
    if (user) {
      setVolunteerForm(prev => ({
        ...prev,
        name: user.name || "",
        email: user.email || ""
      }));
    }
  }, [user]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/payments/donate/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        toast.success(`Thank you for your $${response.data.amount} donation! 🎉`);
      } else {
        toast.info("Payment is being processed. Thank you!");
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
    }
  };

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg.id);
    setDonationAmount(pkg.amount.toString());
  };

  const handleDonate = async () => {
    if (!donationAmount || isNaN(parseFloat(donationAmount)) || parseFloat(donationAmount) < 1) {
      toast.error("Please enter a valid donation amount (minimum $1)");
      return;
    }

    setIsProcessing(true);
    
    try {
      const amount = parseFloat(donationAmount);
      let packageId = "custom";
      const matchingPkg = donationPackages.find(p => p.amount === amount);
      if (matchingPkg) {
        packageId = matchingPkg.id;
      }

      const response = await axios.post(`${API}/payments/donate/checkout`, {
        package_id: packageId,
        custom_amount: packageId === "custom" ? amount : null,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        toast.error("Failed to create checkout session");
      }
    } catch (error) {
      console.error("Donation error:", error);
      toast.error(error.response?.data?.detail || "Failed to process donation");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVolunteerSubmit = async (e) => {
    e.preventDefault();
    
    if (!volunteerForm.name || !volunteerForm.email || !volunteerForm.role) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmittingVolunteer(true);
    try {
      await axios.post(`${API}/volunteer/apply`, volunteerForm);
      toast.success("Thank you for your interest! We'll be in touch soon.");
      setVolunteerForm({ name: "", email: "", role: "", message: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmittingVolunteer(false);
    }
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="Support Us" 
        description="Support the AI Film Society through donations, sponsorship, or volunteering. Help us grow the global AI filmmaking community."
      />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-purple-light mb-4 font-mono">
            Get Involved
          </p>
          <h1 className="font-syne text-4xl md:text-6xl font-bold text-white mb-6">
            Support Us
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Help the AI Film Society grow and thrive. Whether through donations, 
            sponsorship, or volunteering your time, every contribution makes a difference.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex justify-center gap-2 mb-12">
          {[
            { id: "donate", label: "Donate", icon: Heart },
            { id: "sponsor", label: "Sponsor", icon: Gift },
            { id: "volunteer", label: "Volunteer", icon: Users }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-6 py-3 text-sm uppercase tracking-wider transition-all ${
                activeTab === tab.id
                  ? "bg-purple text-white"
                  : "bg-void-surface text-slate-400 hover:text-white border border-white/10"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Donate Section */}
        {activeTab === "donate" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
          >
            <div className="p-8 md:p-12 bg-void-surface border border-white/5">
              <div className="flex items-center gap-3 mb-6">
                <Heart className="w-6 h-6 text-purple-light" />
                <h2 className="font-syne text-2xl font-bold text-white">
                  Make a Donation
                </h2>
              </div>
              <p className="text-slate-300 mb-8">
                Your donation helps us host events, maintain resources, support filmmakers, 
                and grow the AI cinema community. Every contribution makes a difference.
              </p>

              {/* Donation Tiers */}
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {donationPackages.map((pkg) => (
                  <button
                    key={pkg.id}
                    onClick={() => handleSelectPackage(pkg)}
                    data-testid={`donate-pkg-${pkg.id}`}
                    className={`p-4 text-center transition-all ${
                      selectedPackage === pkg.id
                        ? "bg-purple text-white border-purple"
                        : "bg-void border border-white/10 text-white hover:border-purple/50"
                    }`}
                  >
                    <p className="text-2xl font-syne font-bold mb-1">${pkg.amount}</p>
                    <p className="text-xs uppercase tracking-wider opacity-70">{pkg.label}</p>
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              <div className="flex flex-col sm:flex-row items-center gap-4 mb-8">
                <div className="flex items-center gap-2 flex-grow">
                  <span className="text-slate-400 text-lg">$</span>
                  <Input
                    type="number"
                    min="1"
                    value={donationAmount}
                    onChange={(e) => {
                      setDonationAmount(e.target.value);
                      setSelectedPackage(null);
                    }}
                    placeholder="Custom amount"
                    data-testid="custom-donation-input"
                    className="bg-void border-white/20 text-white text-lg"
                  />
                </div>
                <Button
                  onClick={handleDonate}
                  disabled={isProcessing || !donationAmount}
                  data-testid="donate-submit-btn"
                  className="bg-purple hover:bg-purple-hover text-white px-8 py-3 h-auto"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Heart className="w-4 h-4 mr-2" />
                      Donate Now
                    </>
                  )}
                </Button>
              </div>

              <p className="text-xs text-slate-500 text-center">
                Secure payment powered by Stripe. You'll be redirected to complete your donation.
              </p>
            </div>
          </motion.div>
        )}

        {/* Sponsor Section */}
        {activeTab === "sponsor" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
          >
            <div className="p-8 md:p-12 bg-void-surface border border-white/5 text-center">
              <Gift className="w-12 h-12 text-purple-light mx-auto mb-6" />
              <h2 className="font-syne text-2xl font-bold text-white mb-4">
                Become a Sponsor
              </h2>
              <p className="text-slate-300 mb-8 max-w-xl mx-auto">
                Partner with the AI Film Society to reach a global audience of AI filmmakers, 
                tech enthusiasts, and creative professionals. Sponsorship opportunities include 
                workshops, festivals, and exclusive events.
              </p>
              <a
                href="mailto:sam@aiadstudio.com?subject=AI Film Society Sponsorship Inquiry"
                data-testid="sponsor-contact-btn"
                className="inline-flex items-center gap-2 px-8 py-4 bg-purple text-white font-bold uppercase tracking-widest text-xs hover:bg-purple-hover transition-all"
              >
                <Mail className="w-4 h-4" />
                Contact for Sponsorship
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        )}

        {/* Volunteer Section */}
        {activeTab === "volunteer" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-12">
              <h2 className="font-syne text-2xl font-bold text-white mb-4">
                Volunteer Opportunities
              </h2>
              <p className="text-slate-300 max-w-2xl mx-auto">
                Contribute your skills and time to help the AI Film Society grow. 
                We're always looking for passionate volunteers.
              </p>
            </div>

            {/* Volunteer Roles */}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-12">
              {volunteerRoles.map((item, index) => (
                <motion.div
                  key={item.role}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`volunteer-role-${index}`}
                  className="p-4 bg-void-surface border border-white/5 hover:border-purple/30 transition-colors"
                >
                  <h4 className="font-syne font-bold text-white mb-2">{item.role}</h4>
                  <p className="text-sm text-slate-400">{item.desc}</p>
                </motion.div>
              ))}
            </div>

            {/* Volunteer Form */}
            <div className="p-8 bg-void-surface border border-white/5">
              <h3 className="font-syne font-bold text-white text-xl mb-6">
                Apply to Volunteer
              </h3>
              <form onSubmit={handleVolunteerSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Name *</label>
                    <Input
                      value={volunteerForm.name}
                      onChange={(e) => setVolunteerForm({...volunteerForm, name: e.target.value})}
                      placeholder="Your name"
                      data-testid="volunteer-name"
                      className="bg-void border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Email *</label>
                    <Input
                      type="email"
                      value={volunteerForm.email}
                      onChange={(e) => setVolunteerForm({...volunteerForm, email: e.target.value})}
                      placeholder="your@email.com"
                      data-testid="volunteer-email"
                      className="bg-void border-white/10 text-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Area of Interest *</label>
                  <select
                    value={volunteerForm.role}
                    onChange={(e) => setVolunteerForm({...volunteerForm, role: e.target.value})}
                    data-testid="volunteer-role-select"
                    className="w-full px-3 py-2 bg-void border border-white/10 text-white"
                  >
                    <option value="" className="bg-void-surface">Select a role...</option>
                    {volunteerRoles.map((item) => (
                      <option key={item.role} value={item.role} className="bg-void-surface">
                        {item.role}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Message (Optional)</label>
                  <Textarea
                    value={volunteerForm.message}
                    onChange={(e) => setVolunteerForm({...volunteerForm, message: e.target.value})}
                    placeholder="Tell us about your experience and why you'd like to volunteer..."
                    rows={4}
                    data-testid="volunteer-message"
                    className="bg-void border-white/10 text-white"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submittingVolunteer}
                  data-testid="volunteer-submit-btn"
                  className="bg-purple hover:bg-purple-hover text-white"
                >
                  {submittingVolunteer ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Application"
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
